import { ccfRoutes } from '@app/ccf/ccf-routes';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateSupplyChainActivity,
  useCreateSupplyChainFlow,
  useDeleteSupplyChainActivity,
  useGetSupplyChainFlow,
  useUpdateSupplyChainFlow,
} from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import GuideLayout from '@shared/components/guide-layout';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';
import { useCCF } from '../ccf-context';
import { reportSlugs } from '../slugs';
import { distributionFlowSchema } from './schema';

export type DistributionFlowFormValues = z.infer<typeof distributionFlowSchema>;

export type DistributionFlowContext = {
  flowUuid?: string;
  isCreating?: boolean;
  updateMode?: boolean;
  status: 'idle' | 'loading' | 'success' | 'error';
  reportUuid: string;
  loadingFlow?: boolean;
  onSubmit: (data: DistributionFlowFormValues) => Promise<void>;
  onDelete: () => Promise<void>;
  isDeleting?: boolean;
};

export default function DistributionFlowLayout() {
  const { flow_uuid: flowUuid, report_uuid: companyReportUuid } = useParams();
  const navigate = useNavigate();
  const { categories } = useCCF();
  const { pathname } = useLocation();
  const { toast } = useToast();

  const supplyChainLogistics = categories?.find(
    (item) => item.slug === reportSlugs.supplyChainLogistics
  );

  const updateMode = !!flowUuid;

  const {
    mutateAsync: createFlow,
    isLoading: isCreatingFlow,
    status: createFlowStatus,
  } = useCreateSupplyChainFlow();

  const { mutateAsync: updateFlow, isLoading: isUpdatingFlow } =
    useUpdateSupplyChainFlow({
      companyReportUuid: companyReportUuid!,
    });

  const { mutateAsync: deleteDistributionFlow, isLoading: isDeleting } =
    useDeleteSupplyChainActivity(companyReportUuid!);

  const {
    mutateAsync: createSupplyChainActivity,
    isLoading: isCreatingSuppleChainActivity,
  } = useCreateSupplyChainActivity();

  const { data: distributionFlow, isLoading: loadingFlow } =
    useGetSupplyChainFlow(flowUuid!);

  const form = useForm<DistributionFlowFormValues>({
    resolver: zodResolver(distributionFlowSchema),
  });

  const isCreating =
    isCreatingSuppleChainActivity || isCreatingFlow || isUpdatingFlow;

  const onDelete = async () => {
    try {
      if (flowUuid && companyReportUuid && distributionFlow?.uuid) {
        await deleteDistributionFlow({
          logisticsFlowUuid: distributionFlow.uuid,
        });
        toast({
          title: 'Distribution flow deleted',
          variant: 'success',
        });
        navigate(
          generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
            report_uuid: companyReportUuid,
          })
        );
      }
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error deleting flow',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const onSubmit = async (data: DistributionFlowFormValues) => {
    try {
      if (updateMode) {
        await updateFlow({
          logisticsFlowUuid: flowUuid,
          data: {
            name: data.name || '',
            typeOfClients: data.typeOfClients,
            weight:
              typeof data.weight === 'string'
                ? Number(data.weight)
                : data.weight,
            weightOption: data.weightOption,
            coldChainPercentage:
              typeof data.coldChainPercentage === 'string'
                ? Number(data.coldChainPercentage)
                : data.coldChainPercentage,
            segments: data.segments.map((segment) => ({
              ...segment,
              distance:
                typeof segment.distance === 'string'
                  ? Number(segment.distance)
                  : segment.distance,
            })),
          },
        });
      } else {
        const { uuid: flowUuid } = await createFlow({
          data: {
            type: data.type,
            typeOfClients: data.typeOfClients,
            weight:
              typeof data.weight === 'string'
                ? Number(data.weight)
                : data.weight,
            weightOption: data.weightOption,
            coldChainPercentage:
              typeof data.coldChainPercentage === 'string'
                ? Number(data.coldChainPercentage)
                : data.coldChainPercentage,
            segments: data.segments.map((segment) => ({
              ...segment,
              distance:
                typeof segment.distance === 'string'
                  ? Number(segment.distance)
                  : segment.distance,
            })),
          },
        });

        await createSupplyChainActivity({
          companyReportUuid: companyReportUuid!,
          data: {
            logisticsFlow: flowUuid,
          },
        });
      }

      toast({
        title: `Distribution flow ${updateMode ? 'updated' : 'created'}  successfully`,
        variant: 'success',
      });

      navigate(
        generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
          report_uuid: companyReportUuid!,
        })
      );
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error creating flow',
        description: 'Please ensure all values are complete.',
        variant: 'destructive',
      });
    }
  };

  const context: DistributionFlowContext = {
    reportUuid: companyReportUuid!,
    status: createFlowStatus,
    flowUuid,
    isCreating,
    updateMode,
    onSubmit,
    loadingFlow,
    onDelete,
    isDeleting,
  };

  useEffect(() => {
    const typeOfClients = form.getValues('typeOfClients');
    if (
      !pathname.includes('/intro') &&
      !pathname.includes('/about-clients') &&
      !typeOfClients &&
      !updateMode
    ) {
      navigate(
        generatePath(ccfRoutes.DISTRIBUTION_FLOW_INTRO, {
          report_uuid: companyReportUuid,
          flow_uuid: flowUuid || '',
        })
      );
    }
  }, [companyReportUuid, flowUuid, form, navigate, pathname, updateMode]);

  useEffect(() => {
    if (distributionFlow) {
      form.reset({
        name: distributionFlow.name,
        typeOfClients: distributionFlow.typeOfClients,
        type: 'distribution' as const,
        weight: distributionFlow.weight,
        weightOption: distributionFlow.weightOption,
        coldChainPercentage: distributionFlow.coldChainPercentage,
        segments: distributionFlow.segments.map((segment) => ({
          nodeName: segment.nodeName || undefined,
          nodeType: segment.nodeType,
          storageManagedBy: segment.storageManagedBy || 'company',
          distance: Number(segment.distance || 0),
          transportMode: segment.transportMode || '',
          transportManagedBy: segment.transportManagedBy || 'company',
          storageDuration: segment.storageDuration || 'not_stored',
        })),
      });
    }
  }, [form, distributionFlow]);

  return (
    <GuideLayout
      pageName="Distribution flow"
      onClosePath={generatePath(
        supplyChainLogistics?.status === 'unstarted'
          ? ccfRoutes.SUPPLY_CHAIN_LOGISTICS
          : ccfRoutes.SUPPLY_CHAIN_LOGISTICS,
        {
          report_uuid: companyReportUuid,
        }
      )}
      useReferrer={false}
    >
      <Form {...form}>
        <Outlet context={context} />
      </Form>
    </GuideLayout>
  );
}
